import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useMe } from '../api/useMe.ts';
import { INNSPILL } from '../skall/rom.ts';
import { tilgangFra } from '../skall/tilgang.ts';
import { useProdukter } from '../api/useProducts.ts';
import { useOppdaterProgram, useOpprettProgram, useProgram, useProgrammer, type Program, type ProgramCreate, type ProgramUpdate } from '../api/usePrograms.ts';
import { useInnstillinger } from '../api/useSettings.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { programOppforing } from '../status/status.ts';
import { radtekst } from './tilstand.ts';
import { Programside } from './Programside.tsx';
import { Skjema, type ProgramSkjemaModus } from './Skjema.tsx';
import { FOKUS_DOEREN, lesHypoteseNr, NyttFraHypotese } from './FraHypotese.tsx';
import { Kartknapp } from '../innboks/Kartknapp.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import type { Tast } from '../innboks/Tastaturkart.tsx';

type Panelmodus = { navn: 'ingen' } | { navn: 'ny' } | { navn: 'rediger'; id: string };

/** Tellerne i toppen ER filtrene (F13, samme mønster som innboksen). «alle» til noe velges. */
type Filter = 'alle' | 'aapne' | 'utkast' | 'avsluttet';

const FILTRE: Array<[Filter, string]> = [
  ['alle', 'alle'],
  ['aapne', 'åpne'],
  ['utkast', 'utkast'],
  ['avsluttet', 'avsluttet'],
];

const HORER_TIL: Record<Filter, (p: Program) => boolean> = {
  alle: () => true,
  aapne: (p) => p.status === 'aapen' || p.status === 'aktiv' || p.status === 'avsluttes',
  utkast: (p) => p.status === 'utkast',
  avsluttet: (p) => p.status === 'avsluttet' || p.status === 'avbrutt',
};

/**
 * Betaprogrammene (admin-program.jsx 4a–4c, M3.1). Lista til venstre, og til høyre enten
 * skjemaet (nytt eller rediger) eller programsiden med tellinger, invitasjoner og
 * deltakere.
 *
 * Tomtilstanden er en invitasjon til handling (DD-19), aldri «ingen data»: et betaprogram
 * starter når et mønster i innboksen fortjener testere.
 */
/** Programmer-sidens egne taster. Rekkefølgen følger hvor ofte de brukes. */
const TASTER: Tast[] = [
  ['n', 'nytt program'],
  ['j / k', 'neste / forrige program'],
  ['Esc', 'lukk skjemaet — ut av feltet'],
];

export function ProgrammerSide() {
  const me = useMe();
  // Rom-rollen i rommet siden hører til — aldri `role`, som bare sier eier eller medlem (M4.8, `skall/tilgang.ts`).
  const kanSkrive = tilgangFra(me.data).kanSkrive(INNSPILL);
  const produkter = useProdukter();
  const innstillinger = useInnstillinger();
  const liste = useProgrammer({});
  const alleProgrammer = useMemo(() => liste.data?.pages.flatMap((p) => p.items) ?? [], [liste.data]);
  const opprett = useOpprettProgram();
  const oppdater = useOppdaterProgram();
  const opptatt = opprett.isPending || oppdater.isPending;

  // `?program=` åpner ett program (H3.4: «Åpne i Innspill», «Se rapporten», «Utgangspunkt»).
  const [sok] = useSearchParams();
  const lokasjon = useLocation();
  const sti = lokasjon.pathname;
  const naviger = useNavigate();
  const programParam = sok.get('program');
  const [valgtId, setValgtId] = useState<string | null>(programParam);
  useEffect(() => {
    if (programParam) setValgtId(programParam);
  }, [programParam]);
  // /programmer/nytt?fra=H-142: skjemaet åpnet fra hypotesen (H3.4). Adressen eier det, ikke tilstanden.
  const fraNr = sti === '/programmer/nytt' ? lesHypoteseNr(sok.get('fra')) : null;
  const fraTilbake = (lokasjon.state as { tilbake?: string } | null)?.tilbake ?? (fraNr !== null ? `/h/registeret/${fraNr}` : '/h');
  const [panel, setPanel] = useState<Panelmodus>({ navn: 'ingen' });
  const [toast, setToast] = useState<ToastInnhold | null>(null);
  const [filter, setFilter] = useState<Filter>('alle');
  const smal = useSmal();

  // Filteret er en visning, ikke en spørring: lista er kort, og serveren sorterer den alt.
  const programmer = useMemo(() => alleProgrammer.filter(HORER_TIL[filter]), [alleProgrammer, filter]);

  // Detaljen har tellerne; lista har samme form, men detaljen er den ferske.
  const detalj = useProgram(valgtId);
  const valgt: Program | null = detalj.data ?? programmer.find((p) => p.id === valgtId) ?? null;

  useEffect(() => {
    // Til lista er hentet, vet vi ikke hvilke som finnes: et valgt program fra adressen står.
    if (!liste.data) return;
    if (programmer.length === 0) {
      if (valgtId !== null) setValgtId(null);
      return;
    }
    // Under breakpointen (DD-51) er lista det brukeren ser først: ingenting autovelges.
    if (valgtId === null) {
      if (!smal) setValgtId(programmer[0]!.id);
      return;
    }
    if (!programmer.some((p) => p.id === valgtId)) setValgtId(smal ? null : programmer[0]!.id);
  }, [liste.data, programmer, valgtId, smal]);

  const visFeil = useCallback((e: unknown) => {
    setToast({ tekst: erApiFeil(e) ? `Kunne ikke lagre: ${e.brukertekst}` : 'Kunne ikke lagre. Prøv igjen.' });
  }, []);

  const skjemaModus = useMemo<ProgramSkjemaModus | null>(() => {
    if (panel.navn === 'ny') return { navn: 'ny' };
    if (panel.navn === 'rediger') {
      const p = alleProgrammer.find((x) => x.id === panel.id);
      return p ? { navn: 'rediger', program: p } : null;
    }
    return null;
  }, [panel, alleProgrammer]);

  // n nytt, j/k i lista. Aldri mens et felt har fokus; preventDefault så bokstaven
  // ikke havner i feltet som får fokus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mål = e.target as HTMLElement | null;
      const iFelt = mål !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(mål.tagName);
      if (e.key === 'Escape') {
        if (fraNr !== null) {
          void naviger(fraTilbake, { state: FOKUS_DOEREN });
        } else if (skjemaModus) setPanel({ navn: 'ingen' });
        else if (iFelt) mål.blur();
        return;
      }
      if (iFelt || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'n' && kanSkrive && fraNr === null) {
        e.preventDefault();
        setPanel({ navn: 'ny' });
        return;
      }
      if ((e.key === 'j' || e.key === 'k') && programmer.length > 0) {
        const i = programmer.findIndex((p) => p.id === valgtId);
        const neste = programmer[Math.min(programmer.length - 1, Math.max(0, i + (e.key === 'j' ? 1 : -1)))];
        if (neste) setValgtId(neste.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [programmer, valgtId, kanSkrive, skjemaModus, fraNr, fraTilbake, naviger]);

  const leserGrunn = kanSkrive ? null : 'Rollen leser kan ikke opprette programmer';
  const produktnavn = produkter.data?.find((p) => p.id === valgt?.productId)?.name;

  return (
    <>
      <header className="panel-topp">
        <h1 className="panel-h1">Programmer</h1>
        {/* Toppen bærer tittel, tellere som filtrerer og handlinger — aldri en setning
            (ux-flow-architect); tomtilstanden forklarer. */}
        <Knapp label="Nytt program" kbd="n" onClick={() => setPanel({ navn: 'ny' })} grunn={leserGrunn} testid="prg-ny" />
        <Kartknapp tittel="Programmer" taster={TASTER} />
      </header>

      {liste.data && alleProgrammer.length > 0 ? (
        <div className="panel-tellere panel-prg-tellere" role="group" aria-label="Filtre">
          {FILTRE.map(([k, ord]) => {
            const n = alleProgrammer.filter(HORER_TIL[k]).length;
            // Et tall vi ikke har, vises ikke — «alle» står uansett (konseptets skjerm 1 og 3).
            if (n === 0 && k !== 'alle') return null;
            return (
              <button key={k} type="button" className="panel-teller" aria-pressed={filter === k} onClick={() => setFilter(k)} data-testid={`prg-filter-${k}`}>
                <b>{n}</b> {ord}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* DD-51 (B3): under grensen er todelt-visningen én kolonne — lista, så siden eller skjemaet med «Tilbake til lista». */}
      <div className={`panel-prg-todelt${skjemaModus || fraNr !== null ? ' panel-prg-todelt-med-skjema' : ''}${fraNr !== null ? ' panel-prg-todelt-fra' : ''}`} data-visning={smal ? (skjemaModus || fraNr !== null || valgt ? 'detalj' : 'liste') : undefined}>
        <section className="panel-prg-liste" aria-label="Programmer">
          {liste.isPending ? (
            <div role="status" aria-busy="true" aria-label="Laster programmer" className="panel-skjelett">
              {[0, 1].map((i) => (
                <div key={i} className="panel-skjelett-rad">
                  <span className="panel-skjelett-linje" />
                  <span className="panel-skjelett-linje panel-skjelett-kort" />
                </div>
              ))}
            </div>
          ) : liste.isError ? (
            <div role="alert" className="panel-melding panel-melding-feil">
              <Ikon navn="warning-outline" /> Kunne ikke laste programmene. {erApiFeil(liste.error) ? liste.error.brukertekst : 'Sjekk nettet og prøv igjen.'}
              <button type="button" className="panel-knapp-liten" onClick={() => void liste.refetch()}>
                Prøv igjen
              </button>
            </div>
          ) : alleProgrammer.length === 0 ? (
            /* DD-19, F14: én setning og «Nytt program» som fylt knapp — aldri «ingen data». */
            <div className="panel-tomrute" role="status">
              <p className="panel-tomrute-tekst">Et betaprogram starter når et mønster i innboksen fortjener testere.</p>
              <Knapp label="Nytt program" kbd="n" primaer onClick={() => setPanel({ navn: 'ny' })} grunn={leserGrunn} testid="prg-ny-tom" />
            </div>
          ) : programmer.length === 0 ? (
            <div className="panel-tomrute" role="status">
              <p className="panel-tomrute-tekst">Ingen programmer i dette filteret.</p>
              <Knapp label="Vis alle" onClick={() => setFilter('alle')} testid="prg-vis-alle" />
            </div>
          ) : (
            /* Raden (DD-67): ikon i statusens farge · navn i body · ETT tall · én meta-linje.
               Samme form og samme klasser som innboksens rad (R1.3) — ett mønster, ett sted. */
            <ul className="panel-rader">
              {programmer.map((p) => {
                const o = programOppforing(p.status);
                const r = radtekst(p);
                return (
                  <li key={p.id} data-testid={`prg-rad-${p.id}`}>
                    <button
                      type="button"
                      className={`panel-rad${p.id === valgtId ? ' panel-rad-valgt' : ''}`}
                      aria-current={p.id === valgtId ? 'true' : undefined}
                      onClick={() => {
                        setValgtId(p.id);
                        setPanel({ navn: 'ingen' });
                      }}
                      // Etiketten fra temaet, aldri kontraktens enumverdi — det var «aapen» som ble lest opp (B8, DD-50).
                      aria-label={`${p.name}, ${o.etikett}, ${r.meta.slice(1).join(', ')}, ${r.verdi ? `${r.verdi} ${r.ord}` : r.ord}`}
                    >
                      <span className="panel-rad-sporikon panel-prg-radikon" data-status={p.status}>
                        <Ikon navn={o.ikon} />
                      </span>
                      <span className="panel-rad-tekst">{p.name}</span>
                      <span className={`panel-rad-tall${r.varsel ? ' panel-rad-over' : ''}`} data-testid="prg-rad-tall">
                        {r.varsel ? <Ikon navn="time-outline" /> : null}
                        {r.verdi ? <b>{r.verdi}</b> : null} {r.ord}
                      </span>
                      <span className="panel-rad-meta" data-testid="prg-rad-meta">
                        {r.meta.map((m, i) => (
                          <span key={m} className={`panel-rad-ledd${i === 0 ? ' panel-prg-statusord' : ''}`} data-status={i === 0 ? p.status : undefined}>
                            {m}
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {liste.hasNextPage ? (
            <div className="panel-last-mer">
              <button type="button" className="panel-knapp-sekundaer" onClick={() => void liste.fetchNextPage()} disabled={liste.isFetchingNextPage}>
                {liste.isFetchingNextPage ? 'Laster …' : 'Last flere'}
              </button>
            </div>
          ) : null}
        </section>

        {fraNr !== null ? (
          <NyttFraHypotese
            key={`fra-${fraNr}`}
            nr={fraNr}
            produkter={produkter.data ?? []}
            produkterTilstand={produkter.isPending ? 'laster' : produkter.isError ? 'feil' : 'ok'}
            maksKontakterTak={innstillinger.data?.maxContactsPerProgram ?? 3}
            minorsPresent={innstillinger.data?.minorsPresent ?? true}
            innstillingerTilstand={innstillinger.isPending ? 'laster' : innstillinger.isError ? 'feil' : 'ok'}
            onLagret={(ny, nr) => {
              // Først nå finnes programmet. Siden viser det i Utkast, og kvitteringen sier hvor lenken står (skjerm 8).
              setFilter('alle');
              setPanel({ navn: 'ingen' });
              setValgtId(ny.id);
              void naviger(`/programmer?program=${ny.id}`, { replace: true });
              setToast({ tekst: `Utkastet er lagret. H-${nr} viser det under Betaprogram.` });
            }}
          />
        ) : skjemaModus ? (
          <Skjema
            key={skjemaModus.navn === 'rediger' ? skjemaModus.program.id : 'ny'}
            modus={skjemaModus}
            produkter={produkter.data ?? []}
            produkterTilstand={produkter.isPending ? 'laster' : produkter.isError ? 'feil' : 'ok'}
            maksKontakterTak={innstillinger.data?.maxContactsPerProgram ?? 3}
            // Ukjent er strengest: uten innstillinger regnes brukerne som mindreårige til det motsatte er kjent.
            minorsPresent={innstillinger.data?.minorsPresent ?? true}
            innstillingerTilstand={innstillinger.isPending ? 'laster' : innstillinger.isError ? 'feil' : 'ok'}
            kanSkrive={kanSkrive}
            opptatt={opptatt}
            onOpprett={(body: ProgramCreate) => {
              opprett
                .mutateAsync(body)
                .then((ny) => {
                  setValgtId(ny.id);
                  setPanel({ navn: 'rediger', id: ny.id });
                  setToast({ tekst: `«${ny.name}» er lagret som utkast. Åpne det når hypotesen, kriteriet og oppgavene står.` });
                })
                .catch(visFeil);
            }}
            onOppdater={(id: string, body: ProgramUpdate) => {
              oppdater
                .mutateAsync({ id, body })
                .then((p) => {
                  setToast({ tekst: `«${p.name}» er oppdatert.` });
                })
                .catch(visFeil);
            }}
            onAapne={(id: string, body: ProgramUpdate) => {
              oppdater
                .mutateAsync({ id, body })
                .then((p) => {
                  setPanel({ navn: 'ingen' });
                  setToast({ tekst: `«${p.name}» er åpent. Inviter pseudonymer — tokenet vises én gang, og dere sender det selv.` });
                })
                .catch(visFeil);
            }}
            onAvbryt={() => setPanel({ navn: 'ingen' })}
            {...(smal ? { onTilbake: () => setPanel({ navn: 'ingen' }) } : {})}
          />
        ) : valgt ? (
          <Programside
            program={valgt}
            {...(produktnavn ? { produktnavn } : {})}
            kanSkrive={kanSkrive}
            opptatt={opptatt}
            {...(smal ? { onTilbake: () => setValgtId(null) } : {})}
            onRediger={() => setPanel({ navn: 'rediger', id: valgt.id })}
            onAvbryt={(grunn) => {
              oppdater
                .mutateAsync({ id: valgt.id, body: { status: 'avbrutt', cancelReason: grunn } })
                .then((p) => setToast({ tekst: `«${p.name}» er avbrutt. Begrunnelsen er sendt til systemet deres.` }))
                .catch(visFeil);
            }}
          />
        ) : null}
      </div>

      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}
